import { extractWorkspaceId } from './workspaceUtils';

const VIEW_STATE_PREFIX = 'sellAgent_viewState_';

const VIEW_STATE_KEYS = {
  AGENT_VIEW: 'agentView',
  DEALS_VIEW: 'dealsView',
  SINGLE_DEAL_ACCORDION: 'singleDealAccordion',
  SINGLE_AGENT_OPTIONS: 'singleAgentOptions',
  SIDEBAR_STATE: 'sidebarState',
  PIPELINE_COLLAPSED_STAGES: 'pipelineCollapsedStages',
  WORKFLOW_VIEW: 'workflowView',
  BILLING_TAB: 'billingTab',
  SELECTED_PIPELINE: 'selectedPipeline',
  ADMIN_VIEW: 'adminView',
  QUICK_FILTERS: 'quickFilters',
  ADVANCED_FILTERS: 'advancedFilters'
};

class ViewStateManager {
  static getWorkspaceId() {
    return extractWorkspaceId(window.location.pathname);
  }

  static getKey(key) {
    const workspaceId = this.getWorkspaceId();
    if (!workspaceId) return null;
    return `${VIEW_STATE_PREFIX}${workspaceId}_${key}`;
  }

  static get(key, defaultValue = null) {
    try {
      const storageKey = this.getKey(key);
      if (!storageKey) return defaultValue;
      const item = localStorage.getItem(storageKey);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue;
    }
  }

  static set(key, value) {
    try {
      const storageKey = this.getKey(key);
      if (!storageKey) return false;
      localStorage.setItem(storageKey, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      return false;
    }
  }

  static remove(key) {
    try {
      const storageKey = this.getKey(key);
      if (!storageKey) return false;
      localStorage.removeItem(storageKey);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }

  static clearAll() {
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(VIEW_STATE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.error('Error clearing view states:', error);
      return false;
    }
  }

  static getAgentView() {
    return this.get(VIEW_STATE_KEYS.AGENT_VIEW, { isTableView: false });
  }

  static setAgentView(viewData) {
    return this.set(VIEW_STATE_KEYS.AGENT_VIEW, viewData);
  }

  static getDealsView() {
    return this.get(VIEW_STATE_KEYS.DEALS_VIEW, { viewType: 'grid' });
  }

  static setDealsView(viewData) {
    return this.set(VIEW_STATE_KEYS.DEALS_VIEW, viewData);
  }

  static getSingleDealAccordion(dealId) {
    const key = `${VIEW_STATE_KEYS.SINGLE_DEAL_ACCORDION}_${dealId}`;
    return this.get(key, { openSections: [] });
  }

  static setSingleDealAccordion(dealId, accordionData) {
    const key = `${VIEW_STATE_KEYS.SINGLE_DEAL_ACCORDION}_${dealId}`;
    return this.set(key, accordionData);
  }

  static getSingleAgentOptions(agentId) {
    const key = `${VIEW_STATE_KEYS.SINGLE_AGENT_OPTIONS}_${agentId}`;
    return this.get(key, { openOptions: [] });
  }

  static setSingleAgentOptions(agentId, optionsData) {
    const key = `${VIEW_STATE_KEYS.SINGLE_AGENT_OPTIONS}_${agentId}`;
    return this.set(key, optionsData);
  }

  static getSidebarState() {
    return this.get(VIEW_STATE_KEYS.SIDEBAR_STATE, { isOpen: false, expandedItems: [] });
  }

  static setSidebarState(sidebarData) {
    return this.set(VIEW_STATE_KEYS.SIDEBAR_STATE, sidebarData);
  }

  static getPipelineCollapsedStages() {
    return this.get(VIEW_STATE_KEYS.PIPELINE_COLLAPSED_STAGES, {});
  }

  static setPipelineCollapsedStages(collapsedStages) {
    return this.set(VIEW_STATE_KEYS.PIPELINE_COLLAPSED_STAGES, collapsedStages);
  }

  static getWorkflowView() {
    return this.get(VIEW_STATE_KEYS.WORKFLOW_VIEW, { isTableView: true });
  }

  static setWorkflowView(viewData) {
    return this.set(VIEW_STATE_KEYS.WORKFLOW_VIEW, viewData);
  }

  static getBillingTab() {
    return this.get(VIEW_STATE_KEYS.BILLING_TAB, { selectedTab: 0 });
  }

  static setBillingTab(tabData) {
    return this.set(VIEW_STATE_KEYS.BILLING_TAB, tabData);
  }

  static getSelectedPipeline() {
    return this.get(VIEW_STATE_KEYS.SELECTED_PIPELINE, null);
  }

  static setSelectedPipeline(pipelineId) {
    return this.set(VIEW_STATE_KEYS.SELECTED_PIPELINE, pipelineId);
  }

  static getAdminView() {
    return this.get(VIEW_STATE_KEYS.ADMIN_VIEW, { activeView: 'dashboard' });
  }

  static setAdminView(viewData) {
    return this.set(VIEW_STATE_KEYS.ADMIN_VIEW, viewData);
  }

  static getQuickFilters(objectType) {
    const key = `${VIEW_STATE_KEYS['QUICK_FILTERS']}_${objectType}`;
    return this.get(key, { searchText: '', dropdownValues: {}, checkboxValues: {}, dateValues: {}, moreFilterValues: {}, moreFilterAnchors: {} });
  }

  static setQuickFilters(objectType, filterData) {
    const key = `${VIEW_STATE_KEYS['QUICK_FILTERS']}_${objectType}`;
    return this.set(key, filterData);
  }

  static clearQuickFilters(objectType) {
    const key = `${VIEW_STATE_KEYS['QUICK_FILTERS']}_${objectType}`;
    return this.remove(key);
  }

  static getAdvancedFilters(objectType) {
    const key = `${VIEW_STATE_KEYS['ADVANCED_FILTERS']}_${objectType}`;
    return this.get(key, null);
  }

  static setAdvancedFilters(objectType, filterData) {
    const key = `${VIEW_STATE_KEYS['ADVANCED_FILTERS']}_${objectType}`;
    return this.set(key, filterData);
  }

  static clearAdvancedFilters(objectType) {
    const key = `${VIEW_STATE_KEYS['ADVANCED_FILTERS']}_${objectType}`;
    return this.remove(key);
  }

  static clearWorkspaceState() {
    try {
      const workspaceId = this.getWorkspaceId();
      if (!workspaceId) return false;
      const workspacePrefix = `${VIEW_STATE_PREFIX}${workspaceId}_`;
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(workspacePrefix)) localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error('Error clearing workspace state:', error);
      return false;
    }
  }
}

export default ViewStateManager;
export { VIEW_STATE_KEYS };